// src/data/notifications.js
//
// THE IN-APP NOTIFICATION CENTRE, BEHIND AN INTERFACE. Ruling 14 (2026-08-23).
//
// Bob: "App by default, offer options if it's easy enough to do."
//
// ---------------------------------------------------------------------------------------------
// WHAT "BEHIND AN INTERFACE" MEANS HERE, AND WHY THE REQUIREMENTS DOCUMENT SAYS IT TWICE.
//
// data\YCP_LC_GC_WORKSPACE_REQUIREMENTS_2026-08-23.md SS3.1: "The deliverable is an in-app
// notification centre behind an interface; email and SMS are later adapters against that interface.
// DO NOT BUILD THE ADAPTER FIRST."
//
// So this module produces NOTIFICATION RECORDS and nothing else. It sends no email, opens no
// socket, and calls no Cloud Function. A record written to Firestore IS the in-app notification;
// an email adapter, when it exists, will read the same records. Anything here that reached outside
// the database would be the adapter arriving first.
//
// AND IT DOES NOT WRITE. Every function returns data for a caller to write, so the caller can put
// the annotation and its notifications in ONE batch - which SS3.1 requires in terms: "If the
// notification fails, the annotation fails."
// ---------------------------------------------------------------------------------------------

export const CHANNELS = { IN_APP: 'in_app' };

// The kinds that exist today. Each is added when something actually raises it - a vocabulary of
// speculative types would be a list nobody maintains.
export const KINDS = {
  RULE_ADDED: 'rule_added',
  RULE_ANNOTATED: 'rule_annotated',
  // Ruling 89, 2026-08-31. Bob's answer to SS147.6 was "b": an assistance request on a DRAFT becomes
  // a notification carrying NO CLAIM CONTENT, leaving ruling 56 untouched.
  ASSISTANCE_REQUESTED: 'assistance_requested',
  // WO-001 S7 (2026-09-14, section 245). A vacancy's notices, raised by closeOfficeTerm (server) and
  // shaped by vacancyNotices() in src\data\officeSuccession.js: the designated vice is told he acts
  // from today (SS82 lines 132-138 / SS81 lines 32-40); the Secretary of the General Committee is
  // told to notify the PTD; with no vice or no Secretary of record, the gap itself is the notice.
  OFFICE_VACANT_STEP_UP: 'office_vacant_step_up',
  GC_VACANCY_PTD_NOTICE: 'gc_vacancy_ptd_notice',
  OFFICE_VACANT_NO_VICE: 'office_vacant_no_vice',
  // Record 603: raised by decideApplication (functions\index.js, Record 588 / ruling 207.3) when an application is
  // refused - audience general_chair, scoped by committee. It was written by the server and named nowhere here.
  APPLICATION_REFUSED: 'application_refused',
};

// Record 603: what each kind is called on a screen. One entry per kind; the suite holds the two lists together.
export const KIND_LABELS = {
  rule_added: 'Rule added to a claim',
  rule_annotated: 'Rule annotated on a claim',
  assistance_requested: 'Assistance requested on a draft',
  office_vacant_step_up: 'Office vacant - you act from today',
  gc_vacancy_ptd_notice: 'General Chairperson vacancy - notify the PTD',
  office_vacant_no_vice: 'Office vacant - no vice of record',
  application_refused: 'Application refused',
};
export function kindLabel(kind) { return KIND_LABELS[kind] || 'Notice'; }

// WHO IS TOLD. Ruling 13, in Bob's words: an LC who adds or annotates a rule must "be sure to follow
// up with BOTH the claimant AND the GC."
//
// TWO RECIPIENTS, ALWAYS, AND NEITHER IS OPTIONAL. They are returned as separate records rather than
// one record with two addressees, because a notification is read and dismissed by ONE person and a
// shared row cannot carry two read states.
export const AUDIENCES = {
  CLAIMANT: 'claimant',
  GENERAL_CHAIR: 'general_chair',
  // Ruling 89. The FIRST audience addressed to a Local Chairperson, and it is addressed by scopeKey
  // and not by committee - LC scoping is scope-key based, which is why a committee-only rule cannot
  // reproduce inLCScope(). The same reasoning that put scopeKey in the storage metadata at ruling 78.
  LOCAL_CHAIR: 'local_chair',
  // S7. Addressed to a PERSON by uid who holds no office yet (the designated vice, the Secretary).
  // The one audience besides claimant that is a uid, and for the same reason: no seat carries it.
  OFFICE_HOLDER: 'office_holder',
};

// A notification the recipient cannot act on is noise. Every record carries the claim it belongs to
// so the centre can link straight to it.
function base(input) {
  const i = input || {};
  return {
    kind: i.kind,
    audience: i.audience,
    claimId: String(i.claimId || ''),
    committee: String(i.committee || ''),      // scopes the GC's read
    scopeKey: String(i.scopeKey || ''),        // scopes the LC's read (ruling 89)
    recipientUid: i.recipientUid || null,      // scopes the claimant's read
    channel: CHANNELS.IN_APP,
    title: i.title,
    body: i.body,
    actorName: i.actorName || 'Unknown',
    actorRole: i.actorRole || 'unknown',
    createdAt: i.at || new Date().toISOString(),
    readAt: null,
  };
}

// THE PAIR. Returns [claimantRecord, gcRecord] - always two, never one.
//
// IF EITHER CANNOT BE ADDRESSED, THIS RETURNS AN ERROR RATHER THAN ONE NOTIFICATION. SS3.1's rule is
// that a follow-up which can silently not happen is not a follow-up, and half a follow-up is the
// same failure wearing a better number: the GC is told and the member is not, and nobody can see
// from the record that the member was never reached.
export function notifyBoth(input) {
  const i = input || {};
  const errors = [];

  if (!i.claimId) errors.push('A notification must name the claim it belongs to.');
  if (!i.kind) errors.push('A notification must have a kind.');
  if (!i.title || !String(i.title).trim()) errors.push('A notification with no title is not readable.');
  if (!i.body || !String(i.body).trim()) errors.push('A notification with no body tells nobody anything.');

  // The claimant is addressed by uid. A claim whose createdBy is missing cannot reach its member.
  if (!i.claimantUid) {
    errors.push('This claim carries no author uid, so the claimant CANNOT be notified - and ruling 13 '
              + 'requires that he is. Fix the claim record rather than notifying the office alone.');
  }
  // The GC is addressed by committee, because the office turns over and the person does not persist.
  if (!i.committee) {
    errors.push('This claim carries no committee, so the General Chairperson cannot be notified - and '
              + 'ruling 13 requires that he is.');
  }

  if (errors.length) return { ok: false, errors, records: [] };

  const common = {
    kind: i.kind, claimId: i.claimId, committee: i.committee,
    title: i.title, body: i.body,
    actorName: i.actorName, actorRole: i.actorRole, at: i.at,
  };

  return {
    ok: true,
    errors: [],
    records: [
      base({ ...common, audience: AUDIENCES.CLAIMANT, recipientUid: i.claimantUid }),
      // Addressed to the OFFICE, not to a person: recipientUid is null and the committee carries it.
      // Ruling 60's reasoning - a seat outlives whoever sits in it, and a notification addressed to a
      // departed chairperson's uid is a notification nobody receives.
      base({ ...common, audience: AUDIENCES.GENERAL_CHAIR, recipientUid: null }),
    ],
  };
}

export function isUnread(n) {
  return !!n && !n.readAt;
}

// Record 592 (functions audit F14): a notice written from the page now carries the SERVER's clock (a Firestore
// Timestamp - the rules pin it to request.time); one written by a Cloud Function carries an ISO string. Both read
// as ISO text here, so the centre sorts and prints them alike.
export function stampText(v) {
  if (v && typeof v.toDate === 'function') { try { return v.toDate().toISOString(); } catch (e) { return ''; } }
  return String(v || '');
}

// The centre's own ordering: unread first, then newest. An unread notification from last week
// outranks a read one from this morning, because the point of the centre is what still needs you.
export function orderForCentre(list) {
  const l = Array.isArray(list) ? list.slice() : [];
  return l.sort((a, b) => {
    const au = isUnread(a) ? 0 : 1, bu = isUnread(b) ? 0 : 1;
    if (au !== bu) return au - bu;
    return stampText(b.createdAt) < stampText(a.createdAt) ? -1 : 1;   // Record 592 (F14)
  });
}

export function unreadCount(list) {
  return (Array.isArray(list) ? list : []).filter(isUnread).length;
}

// Forensic audit 2026-10-16: MAY THIS ACCOUNT MARK THIS NOTICE READ? The mirror of firestore.rules match /notifications
// "allow update" - its four arms and nothing else. That rule has NO administrator arm: the administrator reads every
// notice, but marking one read is the addressee's act. The centre offered "mark read" on every unread notice, so the
// administrator was offered a button the rule refuses on click. The centre now offers it only where this says yes.
export function mayMarkRead(n, user) {
  if (!n || !user || !user.uid || !isUnread(n)) return false;
  if (n.audience === 'claimant' || n.audience === 'office_holder') return !!n.recipientUid && n.recipientUid === user.uid;
  if (n.audience === 'general_chair') return user.role === 'general_chair' && !!user.committee && n.committee === user.committee;
  if (n.audience === 'local_chair') return user.role === 'local_chair' && Array.isArray(user.scopes) && !!n.scopeKey && user.scopes.includes(n.scopeKey);
  return false;
}

// ---------------------------------------------------------------------------------------------
// RULING 89 - THE ASSISTANCE REQUEST, AND WHY IT CARRIES NOTHING
// ---------------------------------------------------------------------------------------------
//
// SS147.6 put two coherent answers to Bob. (a) an assistance request lifts the draft read-gate for
// the scoped LC - which AMENDS ruling 56. (b) the request becomes a notification carrying NO claim
// content, leaving 56 untouched and the member holding his claim until he files it.
//
// Bob, 2026-08-31: "b = proceed".
//
// WHAT WAS WRONG BEFORE. The button wrote `assistanceRequested: true` onto the claim and told the
// member "Assistance request sent to your Local Chair and General Chair." Nothing was sent: that
// flag is read in exactly one place in src\, the member's own screen. And a third line promised
// "your LC/GC can now view this draft", which firestore.rules:191 forbids outright - everyone but
// the owner and an admin is gated on the claim having LEFT draft (ruling 56).
//
// THE DESIGN, AND THE PART THAT MATTERS IS WHAT IS *NOT* IN THE RECORD.
//
// TITLE AND BODY ARE FIXED CONSTANTS, NOT CALLER TEXT. If the caller could compose the body, a
// member could put the substance of his draft into it and ruling 56 would be defeated through the
// notification instead of through the claim - by the member himself, which is not a defence, because
// what 56 protects is a draft's confidentiality as a matter of record and not merely from the
// office's curiosity. So the strings are pinned HERE and pinned again in the rules clause, and the
// notification centre renders from `kind`. There is no free-text field on this record for claim
// content to travel in.
//
// WHAT IT DOES CARRY, AND EACH ONE IS ARGUED FOR:
//   claimId    an opaque pointer. The LC cannot open it while it is a draft and can the moment it
//              is filed. Holding an id he cannot yet use is not a leak; it is the thread.
//   scopeKey   how an LC is addressed at all. Not a committee - inLCScope() is scope-key based.
//   actorName  WHO is asking. A request for help that does not say who wants it cannot be answered,
//              and the member's own name is not the content of his claim.
//   committee  carried because the existing shape requires it and because it is how the record is
//              found later; it discloses nothing the scopeKey does not.
//
// AND ONE AUDIENCE, NOT TWO. notifyBoth() exists because ruling 13 requires BOTH the claimant and
// the GC to be told about a rule annotation. This is the opposite case: the member is the one
// asking, so notifying him of his own request is noise, and the GC is not who he asked.

export const ASSISTANCE_TITLE = 'Assistance requested on a draft';
export const ASSISTANCE_BODY  = 'A member on your territory has asked for help with a draft claim. '
  + 'The claim itself stays private to him until he submits it for representation.';
// Record 649 (the bubbles review, Record 632.2/632.4): "until he files it" was untrue - a member SUBMITS his claim for
// representation; it is never called a filing (ruling 61.2 table). The words above replace it. The PRIOR words stay
// accepted by firestore.rules so a page still open in somebody's browser from before this deploy is not refused; the
// rules deploy before the new page does (ycp.ps1 step 2c before step 3), so no request is ever refused in between.
export const ASSISTANCE_BODY_PRIOR = 'A member on your territory has asked for help with a draft claim. '
  + 'The claim itself stays private to him until he files it.';

/**
 * Build the single notification record for ruling 89's assistance request.
 * Returns { ok, errors, records } in the same shape as notifyBoth(), so a caller batches it the
 * same way - and SS3.1 still governs: if the notification fails, the flag fails with it.
 */
export function notifyAssistance(input) {
  const i = input || {};
  const errors = [];

  if (!i.claimId) errors.push('An assistance request must name the claim it belongs to.');
  if (!i.scopeKey) errors.push('An assistance request must carry a scopeKey, or no Local Chairperson '
    + 'is addressed by it and it reaches nobody.');
  if (!i.committee) errors.push('An assistance request must carry a committee.');
  if (!i.actorName || !String(i.actorName).trim()) {
    errors.push('An assistance request that does not say WHO is asking cannot be answered.');
  }

  if (errors.length) return { ok: false, errors, records: [] };

  return {
    ok: true,
    errors: [],
    records: [
      base({
        kind: KINDS.ASSISTANCE_REQUESTED,
        audience: AUDIENCES.LOCAL_CHAIR,
        claimId: i.claimId,
        committee: i.committee,
        scopeKey: i.scopeKey,
        recipientUid: null,        // the OFFICE, not a person - ruling 60's reasoning
        title: ASSISTANCE_TITLE,   // pinned. Never the caller's.
        body: ASSISTANCE_BODY,     // pinned. Never the caller's.
        actorName: i.actorName,
        actorRole: 'claimant',
        at: i.at,
      }),
    ],
  };
}

/**
 * Does this record carry anything a caller could have put claim content into? Used by the tests and
 * by anything that wants to refuse to send one rather than find out later.
 */
export function carriesOnlyPinnedText(rec) {
  return !!rec
    && rec.kind === KINDS.ASSISTANCE_REQUESTED
    && rec.title === ASSISTANCE_TITLE
    && rec.body === ASSISTANCE_BODY;
}
